import { CommonModule } from '@angular/common';
import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input,
  OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild, inject, signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UiDialogComponent } from '@template-ui/components';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzDialogData } from '@template-ui/types';
import { WorkflowLibraryCategory, WorkflowLibraryClosedReason, WorkflowLibraryEntry, WorkflowLibraryFooterAction } from './workflow-library-selector.types';

/** Searchable library presentation; every requested choice is revalidated by its owning editor. */
@Component({
  selector: 'phms-workflow-library-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective],
  templateUrl: './workflow-library-selector.component.html',
  styleUrl: './workflow-library-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowLibrarySelectorComponent implements OnChanges, AfterViewInit, OnDestroy {
  private static nextId = 0;
  readonly idPrefix = `workflow-library-${++WorkflowLibrarySelectorComponent.nextId}`;
  @Input() title = 'Choose an item';
  @Input() description = '';
  @Input() searchLabel = 'Search library';
  @Input() allCategoryLabel = 'All types';
  @Input() categories: readonly WorkflowLibraryCategory[] = [];
  @Input() entries: readonly WorkflowLibraryEntry[] = [];
  @Input() open = false;
  @Input() active = true;
  @Input() context = '';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() loadingMessage = 'Loading choices…';
  @Input() error = '';
  @Input() emptyMessage = 'No choices are available.';
  @Input() noResultsMessage = 'No choices match your search and category.';
  @Input() retryLabel = 'Retry';
  @Input() retryDisabled = false;
  @Input() footerMessage = '';
  @Input() footerActions: readonly WorkflowLibraryFooterAction[] = [];
  /** The owner pages/searches a remote inventory; existing libraries retain local filtering. */
  @Input() remoteSearch = false;
  @Input() entryDetailsTemplate: TemplateRef<{ $implicit: WorkflowLibraryEntry }> | null = null;
  @Input() statusTemplate: TemplateRef<unknown> | null = null;
  @Output() readonly searchRequested = new EventEmitter<string>();
  @Output() readonly chooseRequested = new EventEmitter<string>();
  @Output() readonly ownerActionRequested = new EventEmitter<{ id: string; actionId: string }>();
  @Output() readonly footerActionRequested = new EventEmitter<string>();
  @Output() readonly retryRequested = new EventEmitter<void>();
  @Output() readonly opened = new EventEmitter<void>();
  /** Revoke pending owner work when closing starts, before exit animation completes. */
  @Output() readonly closing = new EventEmitter<WorkflowLibraryClosedReason>();
  @Output() readonly closed = new EventEmitter<WorkflowLibraryClosedReason>();
  @ViewChild('content', { static: true }) private content?: TemplateRef<unknown>;
  readonly search = signal('');
  readonly category = signal('');
  private readonly dialog = inject(MatDialog);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private viewReady = false;
  private destroyed = false;
  private ref: MatDialogRef<UiDialogComponent> | null = null;
  private openedContext = '';
  private closeReason: WorkflowLibraryClosedReason | null = null;
  private origin: HTMLElement | null = null;
  private focusReturnRequest = 0;

  ngAfterViewInit(): void { this.viewReady = true; this.syncDialog(); }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.ref) {
      if (!this.active) this.close('inactive');
      else if (changes['context'] && this.context !== this.openedContext) this.close('context-changed');
      else if (!this.open) this.close('input');
      if (changes['title'] && this.ref) this.ref.componentInstance.data.heading = this.title;
    }
    if (this.category() && !this.categories.some(category => category.id === this.category())) this.category.set('');
    this.syncDialog();
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.focusReturnRequest++;
    const ref = this.ref;
    if (ref) {
      this.beginClosing('destroyed');
      this.ref = null;
      this.closeReason = 'destroyed';
      ref.close();
      this.origin = null;
      this.closed.emit('destroyed');
    }
  }

  get visibleEntries(): readonly WorkflowLibraryEntry[] {
    const search = this.search().trim().toLocaleLowerCase(), category = this.category();
    return this.entries.filter(entry => (!category || entry.categoryId === category) &&
      (this.remoteSearch || !search || [entry.label, entry.description, entry.disabledReason].some(value => value?.toLocaleLowerCase().includes(search))));
  }
  searchChanged(value: string): void {
    this.search.set(value);
    if (this.remoteSearch && this.isCurrent() && !this.disabled) this.searchRequested.emit(value);
  }
  readonly trackEntry = (index: number, entry: WorkflowLibraryEntry) => this.entries.filter(candidate => candidate.id === entry.id).length === 1 ? entry.id : `${entry.id}:${index}`;
  readonly trackCategory = (_index: number, category: WorkflowLibraryCategory) => category.id;
  readonly trackFooterAction = (index: number, action: WorkflowLibraryFooterAction) => this.footerActions.filter(candidate => candidate.id === action.id).length === 1 ? action.id : `${action.id}:${index}`;
  entryReason(entry: WorkflowLibraryEntry): string {
    return this.entries.filter(candidate => candidate.id === entry.id).length !== 1
      ? 'This choice identity is ambiguous. Reload the library before choosing it.' : entry.disabledReason ?? '';
  }
  choose(id: string): void {
    const entry = this.currentEntry(id);
    if (entry && !this.entryReason(entry)) this.chooseRequested.emit(id);
  }
  requestOwner(id: string, actionId: string): void {
    const entry = this.currentEntry(id);
    if (entry?.ownerAction?.id === actionId && !entry.ownerAction.disabled) this.ownerActionRequested.emit({ id, actionId });
  }
  footerActionDisabled(action: WorkflowLibraryFooterAction): boolean {
    return this.disabled || this.loading || !!action.disabled || this.footerActions.filter(candidate => candidate.id === action.id).length !== 1;
  }
  requestFooterAction(id: string): void {
    if (!this.isCurrent()) return;
    const actions = this.footerActions.filter(action => action.id === id);
    if (actions.length === 1 && !this.footerActionDisabled(actions[0])) this.footerActionRequested.emit(id);
  }
  retry(): void { if (this.isCurrent() && !this.disabled && !this.retryDisabled) this.retryRequested.emit(); }
  dismiss(): void { this.close('dismissed'); }

  private currentEntry(id: string): WorkflowLibraryEntry | null {
    if (!this.isCurrent() || this.disabled || this.loading) return null;
    const entries = this.entries.filter(entry => entry.id === id);
    return entries.length === 1 ? entries[0] : null;
  }
  private isCurrent(): boolean {
    return !!this.ref && !this.closeReason && !this.destroyed && this.open && this.active && this.context === this.openedContext;
  }
  private syncDialog(): void {
    if (!this.viewReady || this.destroyed || this.ref || !this.open || !this.active || !this.content) return;
    this.focusReturnRequest++;
    this.search.set(''); this.category.set('');
    this.closeReason = null;
    this.openedContext = this.context;
    const active = this.element.nativeElement.ownerDocument.activeElement;
    this.origin = active instanceof HTMLElement ? active : null;
    const ref = this.dialog.open(UiDialogComponent, {
      width: '60rem', maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100dvh - 2rem)',
      autoFocus: `#${this.idPrefix}-search`, restoreFocus: false, disableClose: false,
      ariaLabel: this.title,
      data: { heading: this.title, buttons: [] } satisfies EzDialogData,
    });
    this.ref = ref;
    ref.componentRef?.setInput('contentTemplate', this.content);
    ref.beforeClosed().subscribe(() => {
      if (this.ref === ref) this.beginClosing('dismissed');
    });
    ref.afterClosed().subscribe(() => {
      if (this.ref !== ref) return;
      const reason = this.closeReason ?? 'dismissed', origin = this.origin, context = this.openedContext;
      this.ref = null; this.origin = null;
      this.closed.emit(reason);
      if (reason === 'dismissed' && origin) this.restoreDismissedFocus(origin, context);
    });
    this.opened.emit();
  }
  private close(reason: WorkflowLibraryClosedReason): void {
    const ref = this.ref;
    if (!ref || this.closeReason) return;
    this.beginClosing(reason);
    if (this.ref === ref) ref.close();
  }
  private beginClosing(reason: WorkflowLibraryClosedReason): void {
    if (this.closeReason !== null) return;
    this.closeReason = reason;
    this.closing.emit(reason);
  }
  private restoreDismissedFocus(origin: HTMLElement, context: string): void {
    const request = ++this.focusReturnRequest;
    // Let the owning close callback render away its native inert state first.
    setTimeout(() => {
      if (request !== this.focusReturnRequest || this.destroyed || !this.active || this.context !== context ||
        this.ref || this.dialog.openDialogs.length || !this.canRestoreFocus(origin)) return;
      origin.focus();
    });
  }
  private canRestoreFocus(origin: HTMLElement): boolean {
    if (!origin.isConnected || origin.closest('[hidden], [inert]') || origin.matches(':disabled')) return false;
    for (let element: HTMLElement | null = origin; element; element = element.parentElement) {
      const style = element.ownerDocument.defaultView?.getComputedStyle(element);
      if (style?.display === 'none' || style?.visibility === 'hidden' || style?.visibility === 'collapse') return false;
    }
    return true;
  }
}
